/**
 * ⟦C21 · A-146⟧ `Fenetre` — la fenêtre pixel réutilisable de la direction **F3** (« Fenêtres »),
 * décision de Mathieu du 12/09/2026 après trois maquettes (F1 Pokémon, F2 Matt toujours là, F3
 * Fenêtres) : « F3 ». Bandeau violet, titre en VT323, bordure en escalier (`clip-path` deux
 * couches, système C déjà porté par `BoiteDialogue`), corps tramé (`repeating-conic-gradient`,
 * même trame que `.boite-inner`) — un bouton ▭ de 48 px replie/déplie le corps.
 *
 * Porté depuis `assets/making-of/2026-09-12-espace2-chambre/maquettes/F3/_commun.css`
 * (`.fenetre`, `.fenetre-inner`, `.fenetre-ruban`, `.fenetre-repli`, `.icone-repli`,
 * `.fenetre-corps`), recopié dans `app.css`, pas réinventé.
 *
 * Née ici pour l'écran de la chambre (`Studio.tsx` : « Ma chaîne », « Journal », « Actions »),
 * étendue aux écrans secondaires par ⟦C21 bis · point 0⟧ (la fiche « ce que ça change ») puis
 * généralisée à TOUTES les pop-ups du jeu par ⟦C44 · A-166⟧ : `Panneau.tsx` compose désormais
 * cette même fenêtre plutôt que de dessiner son propre chrome (retour de Matt, 18/09/2026:
 * « on avait dit que toute l'UI était en mode fenêtre »).
 *
 * ⟦C44 · A-166⟧ Deux ajouts pour ce chantier :
 * - `onFermer` pose une **petite croix** à droite du bandeau — le geste de fermeture natif
 *   d'une fenêtre, à la place du gros bouton texte « ✕ Fermer » que chaque écran réinventait
 *   dans son propre en-tête (`Panneau.tsx`) ou dans ses `actions` (`NouvelleRun.tsx`, la fiche
 *   « ce que ça change »).
 * - `pied` pose un bandeau BAS, hors du flux qui défile (`.fenetre-corps` garde son
 *   `overflow-y: auto`, jamais lui) : c'est ce qui garde un bouton d'action (« Lancer la
 *   production ») visible sans défiler, quelle que soit la hauteur du contenu au-dessus.
 */

import type { ReactNode } from "react";

export interface FenetreProps {
  /** Le titre affiché dans le bandeau, en VT323 majuscule (mis en forme par le CSS, pas ici). */
  titre: string;
  /** Une icône optionnelle avant le titre (emoji, jamais une image générée pour si peu). */
  icone?: ReactNode;
  /** `true` : la fenêtre porte un bouton ▭ de repli. `false`/absent : pas de bouton du tout. */
  repliable?: boolean;
  /** État replié — ignoré si `repliable` n'est pas vrai. */
  repliee?: boolean;
  /** Appelé au clic sur ▭. */
  onBasculer?(): void;
  /** Petits boutons additionnels dans la barre, avant la croix de fermeture (⟦C16⟧, prévu pour
   *  un bouton d'action à côté de « Fermer », ex. `Bibliotheque.tsx` n'en pose pas encore). */
  actions?: ReactNode;
  /** ⟦C44 · A-166⟧ Appelé au clic sur la petite croix de fermeture, à droite du bandeau. Absent
   *  (le cas par défaut) : pas de croix — le comportement d'avant ce chantier, pour les fenêtres
   *  du flux de `Studio.tsx` qui ne se « ferment » pas, elles se replient. */
  onFermer?(): void;
  /** Le corps de la fenêtre. Masqué (pas juste caché en CSS) quand `repliee` est vrai : une
   *  fenêtre repliée n'a plus rien à faire défiler ni à annoncer à un lecteur d'écran. */
  children: ReactNode;
  /** Classes additionnelles sur la racine `.fenetre` — sert au placement en grille (`.fen-chaine`
   *  etc., `Studio.tsx`) et au halo pulsé de Matt (`.halo-pulse`, ⟦C21 §2⟧). */
  className?: string;
  /** `id` du corps, pour `aria-controls` sur le bouton de repli. */
  id?: string;
  /** Rôle ARIA optionnel sur la racine — `"dialog"` quand la fenêtre est posée en superposition
   *  par-dessus un écran (⟦C21 bis · point 0⟧, la fiche « ce que ça change »), absent par défaut
   *  pour les fenêtres en flux de `Studio.tsx`. */
  role?: string;
  /** À poser avec `role="dialog"` : ajoute `aria-modal="true"` sur la racine. */
  ariaModal?: boolean;
  /** ⟦C44 · A-166⟧ Un pied de fenêtre, hors du défilement du corps — le bouton « Lancer la
   *  production » (`NouvelleVideo.tsx`) y vit pour rester visible sans défiler, à 1280×800 comme
   *  à 1920×1080. Absent (le cas par défaut), rien ne change : pas de pied, comme avant. */
  pied?: ReactNode;
  /** ⟦C48 · A-168 §3⟧ Le titre du bandeau est aussi le titre de PAGE (`<h1>`) : les écrans de
   *  crise et de fin sont des pages à part entière (`EcranFenetre.tsx`), pas des pop-ups posées
   *  sur la chambre, et une page a un titre de premier niveau. Absent (le cas par défaut) : un
   *  simple `<span>`, comme pour toutes les fenêtres d'avant ce chantier. */
  titreEnH1?: boolean;
  /** ⟦C48 · A-168 §3⟧ `id` de l'élément qui NOMME la boîte de dialogue quand ce n'est pas le titre
   *  du bandeau (l'événement : le bandeau dit la catégorie, le nom du dialogue est le titre de
   *  l'événement, plus bas). Remplace `aria-label` (qui vaut alors le titre du bandeau). */
  ariaLabelledBy?: string;
}

export function Fenetre({
  titre,
  icone,
  repliable = false,
  repliee = false,
  onBasculer,
  actions,
  onFermer,
  children,
  className,
  id,
  role,
  ariaModal,
  pied,
  titreEnH1 = false,
  ariaLabelledBy,
}: FenetreProps) {
  const idCorps = id ? `${id}-corps` : undefined;
  return (
    <section
      className={`fenetre${className ? ` ${className}` : ""}`}
      aria-label={ariaLabelledBy ? undefined : titre}
      aria-labelledby={ariaLabelledBy}
      role={role}
      aria-modal={ariaModal ? "true" : undefined}
    >
      <div className="fenetre-inner">
        <div className="fenetre-ruban">
          {icone ? (
            <span className="fenetre-icone" aria-hidden="true">
              {icone}
            </span>
          ) : null}
          {titreEnH1 ? (
            <h1 className="fenetre-titre">{titre}</h1>
          ) : (
            <span className="fenetre-titre">{titre}</span>
          )}
          {actions}
          {onFermer ? (
            <button
              type="button"
              className="fenetre-fermer-croix"
              title="Fermer"
              aria-label={`Fermer ${titre}`}
              onClick={onFermer}
            >
              <span className="icone-fermer" aria-hidden="true">
                ✕
              </span>
            </button>
          ) : null}
          {repliable ? (
            <button
              type="button"
              className="fenetre-repli"
              title={repliee ? "Déplier" : "Replier"}
              aria-label={repliee ? `Déplier ${titre}` : `Replier ${titre}`}
              aria-expanded={!repliee}
              aria-controls={idCorps}
              onClick={onBasculer}
            >
              <span className="icone-repli" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {repliee ? null : (
          <>
            <div className="fenetre-corps" id={idCorps}>
              {children}
            </div>
            {pied ? <div className="fenetre-pied">{pied}</div> : null}
          </>
        )}
      </div>
    </section>
  );
}
